import { ApprovalsService } from 'api-clients/monolith';
import { useCallback, useMemo } from 'react';

import { useApiService } from 'hooks/useApiService';
import { useApiServiceMutation } from 'hooks/useApiServiceMutation';

import { SubjectType } from '../types';
import { Decision } from './DecisionFooter';
import { toApprovalRequest, toApprovalRequestList } from './normalize';
import { ApprovalRequest, ApprovalRequestList, RequestStatus } from './types';

/**
 * The generated client's payload, mapped to the types this feature reads
 * (AXHE-3947).
 *
 * Normalized on the way OUT of the hook rather than inside the service callback.
 * A `.then()` on a `CancelablePromise` returns a plain Promise, so mapping there
 * would silently strip `cancel()` — and `useApiService` calls it on unmount and
 * on refetch, which would throw instead of aborting the request.
 */
interface NormalizedQuery<T> {
  /** The payload once it has arrived, and the previous one while reloading. */
  data: T | null;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

/**
 * "No filter" as a first-class value rather than `undefined`.
 *
 * Each of the queue's dropdowns has an "All" entry that is a real, selectable
 * choice — it is what the control falls back to and what Clear restores — so
 * modelling it as the absence of a value leaves the menus unable to express
 * their own default. Converted to an omitted query parameter at the boundary
 * below, so the client's sentinel never reaches the wire.
 */
export const ALL = 'all';

export type TypeFilter = typeof ALL | SubjectType;
export type StatusFilter = typeof ALL | RequestStatus;

export interface QueueFilters {
  /** The server's own "assigned to or submitted by me" narrowing. */
  mine: boolean;
  type: TypeFilter;
  status: StatusFilter;
  /** Free text over the opening title and, on an offer, the applicant name. */
  search: string;
  page: number;
}

/**
 * One page of the approvals queue, filtered (AXHE-3934).
 *
 * These were hardcoded to `('pending', 'OfferLetter', true)` while this tab
 * existed only to make AXHE-3947's review panel reachable. That single call was
 * the whole of "no filters, offers only, page one" — parameterising it is what
 * turns the minimum list into the queue.
 *
 * The arguments are positional. Read their order from the generated client
 * rather than from the interface above — the two do not match, and nothing makes
 * them: `search` sits between `mine` and `page` on the wire.
 *
 * Every filter stays server-side, and two of them could not move even if it were
 * convenient. `mine` is a permission boundary rather than a view — the server
 * narrows a non-admin's list whatever they ask for, so the page cannot filter
 * what it was never sent. `status` and `type` are server-side for the same
 * reason paging is: a client-side predicate over one page filters the page, not
 * the queue.
 *
 * There is deliberately still no way to ask for somebody else's queue — `mine`
 * off widens to the account, which the server gates on its own, and carries no
 * user parameter.
 */
export const useApprovalRequests = (
  filters: QueueFilters,
): NormalizedQuery<ApprovalRequestList> => {
  const { mine, type, status, search, page } = filters;

  const { result, refetch } = useApiService(
    useCallback(
      () =>
        ApprovalsService.getInternalApiApprovalsRequests(
          status === ALL ? undefined : status,
          type === ALL ? undefined : type,
          mine,
          // Omitted rather than sent empty: the server treats a blank `search`
          // as no search anyway, and an empty parameter on every unsearched
          // request makes the query string say something it does not mean.
          search === '' ? undefined : search,
          page,
        ),
      [status, type, mine, search, page],
    ),
  );

  // Whenever the result carries a payload, which includes the `reloading` state
  // `useApiService` enters on every refetch — it holds the previous response.
  // Gated on `ready` alone this goes null for the length of every request the
  // page itself starts, and the pager, which is the one control read from the
  // payload rather than from `isLoading`, unmounts under the cursor on each page
  // turn. The rows are unaffected either way: they are gated on `isLoading`, so
  // a reload shows the spinner rather than the previous page's rows.
  const raw = 'data' in result ? result.data : null;
  const data = useMemo(
    () => (raw === null ? null : toApprovalRequestList(raw)),
    [raw],
  );

  return {
    data,
    isLoading: result.isLoading,
    isError: result.isError,
    refetch,
  };
};

/**
 * One request's level tree, for the review panel.
 *
 * `enabled` guards the null id, but nothing passes one today: `OfferReviewPanel`
 * takes a required `requestId` and the queue mounts it conditionally, so the hook
 * only ever exists while a row is open. The guard is the backstop for a caller
 * that keeps the panel mounted instead — note that `useApiService` leaves
 * `result` at its loading initializer when disabled rather than reaching an idle
 * state, so such a caller would read `isLoading` as true while no row is open.
 *
 * The generated method is `…Requests1` — the codegen disambiguates index from
 * show by suffix, since both live at `/requests`. Named here so the suffix
 * appears in exactly one place.
 */
export const useApprovalRequest = (
  id: string | null,
): NormalizedQuery<ApprovalRequest> => {
  const { result, refetch } = useApiService(
    useCallback(
      () => ApprovalsService.getInternalApiApprovalsRequests1(id ?? ''),
      [id],
    ),
    { enabled: id !== null },
  );

  const raw = result.status === 'ready' ? result.data.request : null;
  const data = useMemo(
    () => (raw === null || raw === undefined ? null : toApprovalRequest(raw)),
    [raw],
  );

  return {
    data,
    isLoading: result.isLoading,
    isError: result.isError,
    refetch,
  };
};

export interface DecisionErrorBody {
  message?: string;
}

/**
 * Records one approve or reject.
 *
 * The response is the request as it now stands, so the panel re-renders from it
 * rather than issuing a second GET — which matters because a decision can settle
 * the request, open the next level, or clear the viewer's own actionable step,
 * and all three change what the panel must show.
 */
export const useRecordDecision = (
  id: string,
  options: {
    onSuccess: (request: ApprovalRequest) => void;
    onError: (body: DecisionErrorBody | undefined) => void;
  },
) => {
  const run = useCallback(
    (stepId: number, decision: Decision, comment?: string) =>
      ApprovalsService.postInternalApiApprovalsRequestsDecisions(id, {
        step_id: stepId,
        decision,
        comment,
      }),
    [id],
  );

  return useApiServiceMutation<
    Awaited<ReturnType<typeof run>>,
    typeof run,
    DecisionErrorBody
  >(run, {
    onSuccess: payload => {
      // `{ decision, request }` — the request as it now stands, so the panel
      // re-renders from the write rather than issuing a second GET.
      if (payload.request) {
        options.onSuccess(toApprovalRequest(payload.request));
        return;
      }

      // A 201 whose body carries no `request` is a contract break, and treating
      // it as nothing is the worst reading: the spinner stops, no message
      // appears, and the approver presses again into a 409. Routed to onError so
      // the surface says the decision could not be confirmed — which is true,
      // whatever the server actually recorded.
      options.onError(undefined);
    },
    onError: options.onError,
  });
};
